/**
 * Filings & Reports tab for India / UK companies.
 *
 * Replaces the US-only SEC Filings + Reports + Business Model tab trio with a
 * single merged view backed by the country-agnostic
 * `GET /api/companies/{id}/filings` endpoint, which lists Neo4j FinancialReport
 * nodes already persisted by the India / UK ingestion pipelines.
 *
 * Sections:
 *   1. Summary strip — coverage, source, cumulative revenue
 *   2. Revenue & net-income trend mini-chart (uses recharts)
 *   3. Coverage timeline showing year span + gaps
 *   4. Filings table with YoY % growth pills, source badges, and an action
 *      link (UK: Companies House document; India: Moneycontrol page)
 *
 * Note on action links:
 *   - UK   FinancialReport stores `document_url` → direct iXBRL/PDF link.
 *   - India FinancialReport stores `url_name` (Moneycontrol slug, no per-filing
 *     PDF). Surface as a company-level "View on Moneycontrol" hint when present.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  ExternalLink, FileText, Loader2, AlertCircle, Calendar, Building2,
  TrendingUp, TrendingDown, Minus, ArrowUpRight, ArrowDownRight,
} from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import Card, { CardBody } from '../ui/Card';
import LoadingCard from '../ui/LoadingCard';
import { Badge } from '../ui/Badge';
import { standaloneCompaniesAPI } from '../../lib/api';
import type { FilingListItem } from '../../lib/api';
import { formatCurrencyValue, formatFiscalYear } from './utils/formatters';
import { isPdfReport } from './utils/financeSource';
import type { RegionConfig } from '../../types/financial';

interface CompanyFilingsTabProps {
  companyId: string;
  companyCountry: 'INDIA' | 'UK';
  companyName?: string;
  regionConfig?: RegionConfig | null;
}

const SOURCE_BADGE: Record<string, { label: string; variant: 'info' | 'warning' | 'success' | 'neutral' | 'primary' | 'secondary' }> = {
  companies_house: { label: 'Companies House', variant: 'info' },
  moneycontrol:    { label: 'Moneycontrol',    variant: 'warning' },
  nse:             { label: 'NSE',             variant: 'success' },
  sec:             { label: 'SEC EDGAR',       variant: 'primary' },
  sec_edgar:       { label: 'SEC EDGAR',       variant: 'primary' },
  pdf_extract:     { label: 'PDF',             variant: 'secondary' },
};

/** Every non-PDF row is web-scraped; unknown web sources get a generic label. */
const WEB_FALLBACK_BADGE = { label: 'Web', variant: 'neutral' as const };

function formatDate(value: string | null): string {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return value;
  }
}

function pctChange(current: number | null, previous: number | null): number | null {
  if (current === null || previous === null) return null;
  if (previous === 0) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

function GrowthPill({ pct }: { pct: number | null }) {
  if (pct === null) return null;
  const isPositive = pct > 0;
  const isFlat = Math.abs(pct) < 0.05;
  if (isFlat) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-neutral-400 ml-1.5">
        <Minus className="h-2.5 w-2.5" /> 0%
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ml-1.5 ${
        isPositive ? 'text-green-600' : 'text-red-500'
      }`}
    >
      {isPositive ? <ArrowUpRight className="h-2.5 w-2.5" /> : <ArrowDownRight className="h-2.5 w-2.5" />}
      {isPositive ? '+' : ''}{pct.toFixed(1)}%
    </span>
  );
}

export default function CompanyFilingsTab({
  companyId,
  companyCountry,
  companyName,
  regionConfig,
}: CompanyFilingsTabProps) {
  const [filings, setFilings] = useState<FilingListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // A company can hold BOTH a web-scraped and a PDF-extracted report for the
  // same fiscal year. The table lists both (each labelled by source); every
  // derived figure — chart, YoY pills, coverage, summary strip — uses exactly
  // ONE record per year, chosen by this precedence toggle.
  const [preferredSource, setPreferredSource] = useState<'web' | 'pdf'>('web');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    standaloneCompaniesAPI
      .getCompanyFilings(companyId)
      .then((res) => {
        if (cancelled) return;
        setFilings(res?.filings ?? []);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg = err?.response?.data?.detail ?? err?.message ?? 'Failed to load filings';
        setError(typeof msg === 'string' ? msg : 'Failed to load filings');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [companyId]);

  // Pre-compute the deduped per-year series, chart data, and YoY map.
  const { chartData, yoyByYear, coverage, tableFilings, effectiveIds, effectiveNewestFirst, duplicateYearCount } =
    useMemo(() => {
      // Table order: newest fiscal year first; within a duplicated year the
      // web row sits above the PDF row, so a year's rows group together and
      // read as one labelled pair.
      const table = [...filings].sort(
        (a, b) =>
          (b.fiscal_year ?? 0) - (a.fiscal_year ?? 0) ||
          Number(isPdfReport(a)) - Number(isPdfReport(b))
      );

      // Collapse to ONE record per fiscal year for everything derived (chart,
      // YoY, coverage, summary). When a year has both a web and a PDF report,
      // the precedence toggle decides which one represents the year — the rows
      // themselves are never merged, only the derived series.
      const byYear = new Map<number, FilingListItem>();
      const bothSourceYears = new Set<number>();
      for (const f of table) {
        const year = f.fiscal_year;
        if (typeof year !== 'number') continue;
        const existing = byYear.get(year);
        if (!existing) {
          byYear.set(year, f);
          continue;
        }
        if (isPdfReport(existing) !== isPdfReport(f)) {
          bothSourceYears.add(year);
          if ((preferredSource === 'pdf') === isPdfReport(f)) byYear.set(year, f);
        }
      }
      const ordered = [...byYear.values()].sort((a, b) => (a.fiscal_year ?? 0) - (b.fiscal_year ?? 0));

      const chart = ordered.map((f) => ({
        year: f.fiscal_year,
        label: f.fiscal_year ? formatFiscalYear(f.fiscal_year, regionConfig ?? undefined) : '—',
        revenue: f.revenue ?? null,
        netIncome: f.net_income ?? null,
      }));

      const yoy = new Map<number, { revenue: number | null; netIncome: number | null }>();
      for (let i = 0; i < ordered.length; i++) {
        const cur = ordered[i];
        const prev = ordered[i - 1];
        yoy.set(cur.fiscal_year ?? -1, {
          revenue: prev ? pctChange(cur.revenue, prev.revenue) : null,
          netIncome: prev ? pctChange(cur.net_income, prev.net_income) : null,
        });
      }

      // Coverage: identify the year range and any missing fiscal years.
      const years = ordered.map((f) => f.fiscal_year).filter((y): y is number => typeof y === 'number');
      let span: { min: number; max: number; missing: number[] } | null = null;
      if (years.length > 0) {
        const min = Math.min(...years);
        const max = Math.max(...years);
        const present = new Set(years);
        const missing: number[] = [];
        for (let y = min; y <= max; y++) {
          if (!present.has(y)) missing.push(y);
        }
        span = { min, max, missing };
      }

      return {
        chartData: chart,
        yoyByYear: yoy,
        coverage: span,
        tableFilings: table,
        effectiveIds: new Set(ordered.map((f) => f.report_id)),
        effectiveNewestFirst: [...ordered].reverse(),
        duplicateYearCount: bothSourceYears.size,
      };
    }, [filings, regionConfig, preferredSource]);

  if (loading) {
    return <LoadingCard message="Loading filings…" delayMs={0} />;
  }

  if (error) {
    return (
      <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
        <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
        <div>
          <p className="font-medium">Could not load filings</p>
          <p className="text-xs text-amber-700 mt-0.5">{error}</p>
        </div>
      </div>
    );
  }

  if (filings.length === 0) {
    return (
      <Card>
        <CardBody className="text-center py-12">
          <FileText className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-neutral-700">No filings recorded yet</p>
          <p className="text-xs text-neutral-500 mt-1">
            Run the {companyCountry === 'UK' ? 'Companies House' : 'BSE/NSE'} research pipeline
            from the “Research New Company” dialog to ingest filings for this company.
          </p>
        </CardBody>
      </Card>
    );
  }

  // Summary figures come from the deduped per-year series — summing the raw
  // rows would double-count any year that has both a web and a PDF report.
  const totalRevenue = effectiveNewestFirst.reduce((sum, f) => sum + (f.revenue ?? 0), 0);
  const yearsCovered = effectiveNewestFirst.length;
  const latestFiscalYear = effectiveNewestFirst[0]?.fiscal_year ?? null;
  const latestRevenue = effectiveNewestFirst[0]?.revenue ?? null;
  const previousRevenue = effectiveNewestFirst[1]?.revenue ?? null;
  const latestRevenueGrowth = pctChange(latestRevenue, previousRevenue);

  // Chart axis tick formatter for currency unit
  const unitAbbr = regionConfig?.currency?.unit_abbr ?? '';
  const symbol = regionConfig?.currency?.symbol ?? '';

  return (
    <div className="space-y-4">
      {/* Summary strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="bg-neutral-50/60">
          <CardBody className="flex items-center gap-3 p-4">
            <Calendar className="h-5 w-5 text-neutral-400" />
            <div>
              <div className="text-[11px] uppercase tracking-wide text-neutral-500">Years covered</div>
              <div className="text-base font-semibold text-neutral-900">
                {yearsCovered} {yearsCovered === 1 ? 'year' : 'years'}
                {latestFiscalYear ? (
                  <span className="text-neutral-500 text-xs font-normal ml-2">
                    latest {formatFiscalYear(latestFiscalYear, regionConfig ?? undefined)}
                  </span>
                ) : null}
              </div>
            </div>
          </CardBody>
        </Card>
        <Card className="bg-neutral-50/60">
          <CardBody className="flex items-center gap-3 p-4">
            <Building2 className="h-5 w-5 text-neutral-400" />
            <div>
              <div className="text-[11px] uppercase tracking-wide text-neutral-500">Source</div>
              <div className="text-base font-semibold text-neutral-900">
                {companyCountry === 'UK' ? 'Companies House (iXBRL)' : 'BSE / NSE / Moneycontrol'}
              </div>
            </div>
          </CardBody>
        </Card>
        <Card className="bg-neutral-50/60">
          <CardBody className="flex items-center gap-3 p-4">
            {latestRevenueGrowth !== null && latestRevenueGrowth >= 0 ? (
              <TrendingUp className="h-5 w-5 text-green-500" />
            ) : latestRevenueGrowth !== null ? (
              <TrendingDown className="h-5 w-5 text-red-500" />
            ) : (
              <TrendingUp className="h-5 w-5 text-neutral-400" />
            )}
            <div>
              <div className="text-[11px] uppercase tracking-wide text-neutral-500">Latest revenue YoY</div>
              <div className="text-base font-semibold text-neutral-900 flex items-center">
                {latestRevenueGrowth !== null
                  ? `${latestRevenueGrowth > 0 ? '+' : ''}${latestRevenueGrowth.toFixed(1)}%`
                  : '—'}
                <span className="text-neutral-500 text-xs font-normal ml-2">
                  cumulative {totalRevenue ? formatCurrencyValue(totalRevenue, regionConfig ?? undefined, true) : '—'}
                </span>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Trend chart */}
      {chartData.length >= 2 && (
        <Card>
          <div className="px-5 pt-4 pb-2">
            <h3 className="text-sm font-semibold text-neutral-900">Revenue & Net Income Trend</h3>
            <p className="text-xs text-neutral-500 mt-0.5">
              {companyCountry === 'UK' ? 'GBP Millions' : 'INR crores'} · across {chartData.length} reporting period{chartData.length === 1 ? '' : 's'}
            </p>
          </div>
          <div className="px-2 pb-4 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: '#a3a3a3' }}
                  axisLine={{ stroke: '#e5e5e5' }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: '#a3a3a3' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v: number) => {
                    if (Math.abs(v) >= 1_000_000) return `${symbol}${(v / 1_000_000).toFixed(1)}M`;
                    if (Math.abs(v) >= 1_000) return `${symbol}${(v / 1_000).toFixed(1)}K`;
                    return `${symbol}${v}`;
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e5e5e5',
                    borderRadius: 8,
                    fontSize: 12,
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.08)',
                  }}
                  formatter={(value: number, name: string) => [
                    formatCurrencyValue(value, regionConfig ?? undefined, true),
                    name === 'revenue' ? 'Revenue' : 'Net Income',
                  ]}
                  labelStyle={{ fontWeight: 600, marginBottom: 4 }}
                />
                <Legend
                  wrapperStyle={{ fontSize: 11, paddingTop: 4 }}
                  formatter={(v: string) => (v === 'revenue' ? 'Revenue' : 'Net Income')}
                />
                <Bar dataKey="revenue" name="revenue" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={36} />
                <Line
                  type="monotone"
                  dataKey="netIncome"
                  name="netIncome"
                  stroke="#10b981"
                  strokeWidth={2}
                  dot={{ r: 3, fill: '#10b981', strokeWidth: 0 }}
                  activeDot={{ r: 5 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Coverage timeline — horizontal line with dots, year labels below */}
      {coverage && (coverage.max - coverage.min) >= 1 && (
        <Card>
          <CardBody className="p-5">
            <div className="flex items-start justify-between mb-5">
              <div>
                <h4 className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">Coverage Timeline</h4>
                <p className="text-[11px] text-neutral-500 mt-0.5">
                  FY{String(coverage.min).slice(-2)} → FY{String(coverage.max).slice(-2)}
                  <span className="text-neutral-400 ml-1">({coverage.max - coverage.min + 1} year{coverage.max - coverage.min === 0 ? '' : 's'})</span>
                  {coverage.missing.length > 0 && (
                    <span className="text-amber-600 ml-2">
                      · {coverage.missing.length} missing
                    </span>
                  )}
                </p>
              </div>
              {coverage.missing.length === 0 ? (
                <Badge variant="success" size="sm">Continuous</Badge>
              ) : (
                <Badge variant="warning" size="sm">Has gaps</Badge>
              )}
            </div>

            {(() => {
              const total = coverage.max - coverage.min + 1;
              const years = Array.from({ length: total }, (_, i) => coverage.min + i);
              return (
                <div className="relative px-2 pb-1">
                  {/* Continuous baseline */}
                  <div className="absolute left-2 right-2 top-3 h-0.5 bg-neutral-200 rounded-full" />
                  {/* Filled progress segment (between earliest and latest filing) */}
                  <div className="absolute left-2 right-2 top-3 h-0.5 rounded-full bg-gradient-to-r from-primary-500 to-primary-400" />
                  {/* Year nodes */}
                  <div className="relative grid" style={{ gridTemplateColumns: `repeat(${total}, minmax(0, 1fr))` }}>
                    {years.map((y) => {
                      const isMissing = coverage.missing.includes(y);
                      return (
                        <div
                          key={y}
                          className="flex flex-col items-center"
                          title={isMissing ? `FY${String(y).slice(-2)} — no filing on record` : `FY${String(y).slice(-2)} — filed`}
                        >
                          <div
                            className={`relative z-10 h-6 w-6 rounded-full border-2 flex items-center justify-center transition-transform ${
                              isMissing
                                ? 'bg-white border-amber-400 border-dashed'
                                : 'bg-primary-600 border-white shadow-sm shadow-primary-200 ring-2 ring-primary-100'
                            }`}
                          >
                            {!isMissing && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
                          </div>
                          <span className={`text-[10px] font-medium tabular-nums mt-2 ${
                            isMissing ? 'text-amber-600' : 'text-neutral-700'
                          }`}>
                            FY{String(y).slice(-2)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Legend */}
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-neutral-100">
              <div className="flex items-center gap-1.5 text-[11px] text-neutral-600">
                <div className="h-3 w-3 rounded-full bg-primary-600 ring-2 ring-primary-100" />
                Filing on record
              </div>
              {coverage.missing.length > 0 && (
                <div className="flex items-center gap-1.5 text-[11px] text-neutral-600">
                  <div className="h-3 w-3 rounded-full bg-white border-2 border-dashed border-amber-400" />
                  Missing year
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Filings table */}
      <Card>
        <div className="px-4 py-3 border-b border-neutral-100 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-sm font-semibold text-neutral-900">Filings & Reports</h3>
            <p className="text-xs text-neutral-500 mt-0.5">
              {filings.length} report{filings.length === 1 ? '' : 's'} on file{companyName ? ` for ${companyName}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Only when a fiscal year genuinely has both sources — otherwise
                there is nothing to choose between. */}
            {duplicateYearCount > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-neutral-500">
                  {duplicateYearCount} year{duplicateYearCount === 1 ? ' has' : 's have'} both sources · charts & totals use
                </span>
                <div className="inline-flex rounded-md border border-neutral-200 bg-white p-0.5">
                  {(['web', 'pdf'] as const).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setPreferredSource(s)}
                      className={`px-2 py-0.5 text-[11px] font-semibold rounded transition-colors ${
                        preferredSource === s
                          ? 'bg-primary-600 text-white'
                          : 'text-neutral-500 hover:text-neutral-700'
                      }`}
                    >
                      {s === 'web' ? 'Web' : 'PDF'}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <Badge variant="neutral" size="sm">{companyCountry}</Badge>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-neutral-50 border-b border-neutral-200">
                <th className="text-left py-2.5 px-4 text-[11px] font-bold text-neutral-500 uppercase tracking-wider">Fiscal Period</th>
                <th className="text-left py-2.5 px-4 text-[11px] font-bold text-neutral-500 uppercase tracking-wider">Period End</th>
                <th className="text-left py-2.5 px-4 text-[11px] font-bold text-neutral-500 uppercase tracking-wider">Form Type</th>
                <th className="text-left py-2.5 px-4 text-[11px] font-bold text-neutral-500 uppercase tracking-wider">Source</th>
                <th className="text-right py-2.5 px-4 text-[11px] font-bold text-neutral-500 uppercase tracking-wider">Revenue</th>
                <th className="text-right py-2.5 px-4 text-[11px] font-bold text-neutral-500 uppercase tracking-wider">Net Income</th>
                <th className="text-right py-2.5 px-4 text-[11px] font-bold text-neutral-500 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody>
              {tableFilings.map((f) => {
                const isPdf = isPdfReport(f);
                // Every row gets a source label — "PDF" for extracted reports,
                // the named web source (or a generic "Web") otherwise. Rows for
                // a duplicated fiscal year are adjacent and distinguished only
                // by this label, never merged.
                const sourceMeta = isPdf
                  ? SOURCE_BADGE.pdf_extract
                  : (f.source ? SOURCE_BADGE[f.source.toLowerCase()] : null) ?? WEB_FALLBACK_BADGE;
                const periodLabel = f.fiscal_period
                  || (f.fiscal_year ? formatFiscalYear(f.fiscal_year, regionConfig ?? undefined) : '—');
                // YoY pills only on the row that represents its year in the
                // deduped series — the pill is computed from that series, so
                // showing it beside the other source's figures would be wrong.
                const yoy = effectiveIds.has(f.report_id)
                  ? yoyByYear.get(f.fiscal_year ?? -1)
                  : undefined;

                const linkUrl = f.document_url
                  || (companyCountry === 'INDIA' && f.source_url
                        ? (f.source_url.startsWith('http') ? f.source_url : null)
                        : null);
                const linkLabel = companyCountry === 'UK'
                  ? 'View Annual Accounts'
                  : 'View on Moneycontrol';

                return (
                  <tr key={f.report_id} className="border-b border-neutral-100 hover:bg-neutral-50/50 transition-colors">
                    <td className="py-3 px-4 text-sm font-medium text-neutral-900">{periodLabel}</td>
                    <td className="py-3 px-4 text-sm text-neutral-600 tabular-nums">{formatDate(f.period_end_date)}</td>
                    <td className="py-3 px-4 text-sm text-neutral-700">{f.form_type ?? '—'}</td>
                    <td className="py-3 px-4 text-sm">
                      <Badge variant={sourceMeta.variant} size="sm">{sourceMeta.label}</Badge>
                    </td>
                    <td className="py-3 px-4 text-sm text-neutral-900 text-right tabular-nums whitespace-nowrap">
                      {formatCurrencyValue(f.revenue, regionConfig ?? undefined, true)}
                      <GrowthPill pct={yoy?.revenue ?? null} />
                    </td>
                    <td className="py-3 px-4 text-sm text-neutral-900 text-right tabular-nums whitespace-nowrap">
                      {formatCurrencyValue(f.net_income, regionConfig ?? undefined, true)}
                      <GrowthPill pct={yoy?.netIncome ?? null} />
                    </td>
                    <td className="py-3 px-4 text-right">
                      {linkUrl ? (
                        <a
                          href={linkUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-800 hover:underline"
                          title={linkLabel}
                        >
                          {linkLabel}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <span className="text-xs text-neutral-300">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footnote — explain India per-filing URL gap */}
        {companyCountry === 'INDIA' && (
          <div className="px-4 py-2.5 bg-neutral-50/60 border-t border-neutral-100">
            <p className="text-[11px] text-neutral-500">
              Per-filing document URLs are not available for Moneycontrol-sourced data. Links open the
              company's Moneycontrol page where individual reports can be viewed.
            </p>
          </div>
        )}
      </Card>

      {/* Stale-while-revalidate indicator */}
      {loading && filings.length > 0 && (
        <div className="flex items-center gap-2 text-xs text-neutral-500 px-1">
          <Loader2 className="h-3 w-3 animate-spin" /> Refreshing…
        </div>
      )}
    </div>
  );
}
